import { FormControlLabel, InputAdornment, Switch } from '@mui/material'
import { Card, Input } from '@nextui-org/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AmountSlider } from '../amountslider/AmountSlider'
import MainButton from '../common/util/input/MainButton'
import { floatRegEx } from '../editor/EditorComponents'
import { get } from '../helpers/Api'
import { formatNumber, formatWAX, sleep } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import LoadingIndicator from '../loadingindicator/LoadingIndicator'
import { PopupLoadingIndicator } from '../loadingindicator/PopupLoadingIndicator'
import ErrorMessage from './ErrorMessage'
import Popup from './Popup'
import { useSharedState } from '../waxplorer/Waxplorer'

function SellPopup(props) {
    const asset = props['asset']
    const { t } = useTranslation('common')

    const {
        assetId,
        schema,
        collection,
        name,
        image,
        contract,
        symbol,
        amount,
        template,
    } = asset

    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [allAssets, setAllAssets] = useState(null)

    const callBack = props['callBack']

    const getMarket = (asset) => {
        let contract = asset['contract']

        if (!contract && asset['assets']) {
            contract = asset['assets'][0]['contract']
        }

        if (contract === 'atomicassets') return 'atomicmarket'

        if (contract === 'simpleassets' && !symbol) return 'waxplorercom'

        return 'nft.hive'
    }

    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState()
    const closeCallBack = props['closeCallBack']
    const [collectionFee, setCollectionFee] = useState(null)
    const [sellPrice, setSellPrice] = useState(0)
    const [sellAmount, setSellAmount] = useState(1)
    const [amountToSell, setAmountToSell] = useState(0)
    const [sellMultiple, setSellMultiple] = useState(false)

    const getCollectionFee = (collection) => {
        get('collection-fee/' + collection, 'wax-api').then((result) =>
            setCollectionFee(result['fee']),
        )
    }

    const getAllOwned = (assetId) => {
        get('get-all-owned/' + assetId, 'legacy').then((result) =>
            setAllAssets(result),
        )
    }

    useEffect(() => {
        if (assetId && !symbol) {
            getAllOwned(assetId)
        }
    }, [assetId])

    useEffect(() => {
        getCollectionFee(collection.collectionName)
    }, [])

    const maxActions = 10

    const multiSell = async () => {
        if (!sellPrice) return

        const quantity = parseFloat(sellPrice)

        setIsLoading(true)

        let currentAction = 0

        const soldAssets = []
        const errors = {}

        const assetsToSell = {}
        assetsToSell[asset['assetId']] = asset
        let index = 0
        while (Object.keys(assetsToSell).length < amountToSell) {
            if (allAssets[index].assetId !== asset['assetId']) {
                assetsToSell[allAssets[index].assetId] = allAssets[index]
            }
            index++
        }

        let sellError = null

        let marketplace = getMarket(asset)

        while (currentAction < amountToSell / maxActions) {
            const start = currentAction * maxActions
            const end = start + maxActions
            const actions = [getBoostAction(activeUser)]

            Object.keys(assetsToSell)
                .sort((a, b) => parseFloat(a.assetId) - parseFloat(b.assetId))
                .slice(start, end)
                .map((assetId) => {
                    const asset = assetsToSell[assetId]

                    marketplace = getMarket(asset)

                    if (marketplace === 'atomicmarket') {
                        actions.push({
                            account: 'atomicmarket',
                            name: 'announcesale',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                seller: userName,
                                maker_marketplace:
                                    process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                                        ? 'nfthiveboost'
                                        : 'nft.hive',
                                settlement_symbol: '8,WAX',
                                asset_ids: [asset.assetId],
                                listing_price: quantity.toFixed(8) + ' WAX',
                            },
                        })
                        actions.push({
                            account: 'atomicassets',
                            name: 'createoffer',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                sender: userName,
                                recipient: 'atomicmarket',
                                sender_asset_ids: [asset.assetId],
                                recipient_asset_ids: [],
                                memo: 'sale',
                            },
                        })
                    } else {
                        actions.push({
                            account: 'waxplorercom',
                            name: 'announcesale',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                seller: userName,
                                asset_ids: [assetId],
                                listing_price: quantity.toFixed(8) + ' WAX',
                            },
                        })
                        actions.push({
                            account: 'simpleassets',
                            name: 'transfer',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                from: userName,
                                to: 'waxplorercom',
                                assetids: [assetId],
                                memo: 'sale',
                            },
                        })
                    }
                })

            try {
                const result = await activeUser.session.transact(
                    {
                        actions: actions,
                    },
                    {
                        expireSeconds: 300,
                        blocksBehind: 0,
                    },
                )
                Object.keys(assetsToSell)
                    .sort((a, b) => parseInt(a.assetId) - parseInt(b.assetId))
                    .slice(start, end)
                    .map((assetId) => {
                        marketplace = getMarket(assetsToSell[assetId])
                        const listingId = getListingId(result, assetId)
                        soldAssets.append({
                            assetId: assetId,
                            market: marketplace,
                            price: quantity,
                            currency: 'WAX',
                            listingId: listingId,
                            transactionId: result?.response?.transaction_id,
                        })
                    })
            } catch (e) {
                Object.keys(assetsToSell)
                    .sort((a, b) => parseInt(a.assetId) - parseInt(b.assetId))
                    .slice(start, end)
                    .map((assetId) => {
                        errors[assetId] = {
                            message: e.message,
                            summaryId: assetsToSell[assetId].summaryId,
                        }
                    })
                console.error(e)
                setError(e.message)
                sellError = e
            } finally {
                currentAction += 1
                if (currentAction < amountToSell / maxActions) {
                    sleep(2000)
                }
            }
        }
        setIsLoading(false)
        if (!sellError) {
            callBack({
                success: true,
                listings: soldAssets,
                market: marketplace,
                price: quantity,
                transactionId:
                    soldAssets && soldAssets.length > 0
                        ? soldAssets[0].transactionId
                        : null,
            })
            closeCallBack()
        }
    }

    const getListingId = (result, assetId) => {
        if (result?.response?.processed?.action_traces) {
            for (const action of result.response.processed.action_traces) {
                if (
                    action.act.name === 'lognewsale' &&
                    action.act.data.asset_ids.includes(assetId.toString())
                ) {
                    return action.act.data.sale_id
                }
            }
        }

        return null
    }

    const sell = async () => {
        if (!sellPrice) return
        const quantity = parseFloat(sellPrice)
        let { assetId } = asset
        if (!assetId && asset['assets']) {
            assetId = asset['assets'][0]['assetId']
        }

        closeCallBack()
        setIsLoading(true)

        const marketplace = getMarket(asset)

        try {
            let result
            switch (marketplace) {
                case 'waxplorercom':
                    result = await activeUser.session.transact(
                        {
                            actions: [
                                getBoostAction(activeUser),
                                {
                                    account: 'waxplorercom',
                                    name: 'announcesale',
                                    authorization: [
                                        {
                                            actor: userName,
                                            permission:
                                                activeUser.requestPermission
                                                    ? activeUser.requestPermission
                                                    : 'active',
                                        },
                                    ],
                                    data: {
                                        seller: userName,
                                        asset_ids: [assetId],
                                        listing_price:
                                            quantity.toFixed(8) + ' WAX',
                                    },
                                },
                                {
                                    account: 'simpleassets',
                                    name: 'transfer',
                                    authorization: [
                                        {
                                            actor: userName,
                                            permission:
                                                activeUser.requestPermission
                                                    ? activeUser.requestPermission
                                                    : 'active',
                                        },
                                    ],
                                    data: {
                                        from: userName,
                                        to: 'waxplorercom',
                                        assetids: [assetId],
                                        memo: 'sale',
                                    },
                                },
                            ],
                        },
                        {
                            expireSeconds: 300,
                            blocksBehind: 0,
                        },
                    )
                    break
                case 'nft.hive':
                    const actions = [getBoostAction(activeUser)]

                    for (let i = 0; i < sellAmount; ++i) {
                        actions.push({
                            account: contract,
                            name:
                                contract === 'simpleassets'
                                    ? 'transferf'
                                    : 'transfer',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data:
                                contract === 'simpleassets'
                                    ? {
                                          from: userName,
                                          to: 'nft.hive',
                                          author: 'a.rplanet',
                                          quantity:
                                              (1.0).toFixed(4) + ' ' + symbol,
                                          memo: 'NFTHive List',
                                      }
                                    : {
                                          from: userName,
                                          to: 'nft.hive',
                                          symbol: symbol,
                                          quantity: 1 + ' ' + symbol,
                                          memo: 'NFTHive List',
                                      },
                        })
                        actions.push({
                            account: 'nft.hive',
                            name: 'createsale',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data:
                                contract === 'simpleassets'
                                    ? {
                                          seller: userName,
                                          quantity:
                                              (1.0).toFixed(4) + ' ' + symbol,
                                          listing_price:
                                              quantity.toFixed(8) + ' WAX',
                                      }
                                    : {
                                          seller: userName,
                                          quantity: 1 + ' ' + symbol,
                                          listing_price:
                                              quantity.toFixed(8) + ' WAX',
                                      },
                        })
                    }
                    result = await activeUser.session.transact(
                        {
                            actions: actions,
                        },
                        {
                            expireSeconds: 300,
                            blocksBehind: 0,
                        },
                    )
                    break
                case 'atomicmarket':
                    result = await activeUser.session.transact(
                        {
                            actions: [
                                getBoostAction(activeUser),
                                {
                                    account: 'atomicmarket',
                                    name: 'announcesale',
                                    authorization: [
                                        {
                                            actor: userName,
                                            permission:
                                                activeUser.requestPermission
                                                    ? activeUser.requestPermission
                                                    : 'active',
                                        },
                                    ],
                                    data: {
                                        seller: userName,
                                        maker_marketplace:
                                            process.env.NEXT_PUBLIC_TESTNET ===
                                            'TRUE'
                                                ? 'nfthiveboost'
                                                : 'nft.hive',
                                        settlement_symbol: '8,WAX',
                                        asset_ids: [assetId],
                                        listing_price:
                                            quantity.toFixed(8) + ' WAX',
                                    },
                                },
                                {
                                    account: 'atomicassets',
                                    name: 'createoffer',
                                    authorization: [
                                        {
                                            actor: userName,
                                            permission:
                                                activeUser.requestPermission
                                                    ? activeUser.requestPermission
                                                    : 'active',
                                        },
                                    ],
                                    data: {
                                        sender: userName,
                                        recipient: 'atomicmarket',
                                        sender_asset_ids: [assetId],
                                        recipient_asset_ids: [],
                                        memo: 'sale',
                                    },
                                },
                            ],
                        },
                        {
                            expireSeconds: 300,
                            blocksBehind: 0,
                        },
                    )
                    break
            }

            let listingId = getListingId(result, assetId)
            callBack({
                success: true,
                listings: [
                    {
                        assetId: assetId,
                        market: marketplace,
                        seller: userName,
                        currency: 'WAX',
                        price: quantity,
                        listingId: listingId,
                    },
                ],
                price: quantity,
                transactionId: result?.response?.transaction_id,
            })
        } catch (e) {
            callBack({
                success: false,
                listings: null,
                price: quantity,
                error: e.message ? e.message : e,
            })
            console.error(e)
        } finally {
            setIsLoading(false)
        }
    }

    const cancel = () => {
        callBack({ success: false, listings: null, price: 0 })
        closeCallBack()
    }

    const changePrice = (e) => {
        const val = e.target.value
        if (/^\d*\.?\d*$/.test(val)) setSellPrice(val)
    }

    const changeAmount = (e) => {
        const val = e.target.value
        if (/^\d*$/.test(val)) setSellAmount(Math.min(val, amount))
    }

    const changeAmountToSell = (e) => {
        setAmountToSell(e.target.value)
    }

    const increaseAmountToSell = () => {
        setAmountToSell(Math.min(amountToSell + 1, allAssets.length))
    }

    const decreaseAmountToSell = () => {
        setAmountToSell(Math.max(amountToSell - 1, 0))
    }

    let cut = sellPrice - 0.03 * sellPrice
    if (collectionFee) cut = cut - collectionFee * sellPrice

    const changeSellMultiple = (e) => {
        setSellMultiple(e.target.checked)
        setAmountToSell(0)
    }

    return (
        <Popup title={name} cancel={cancel} image={image} asset={asset}>
            <div>
                <Card className="p-4 mb-4 ">
                    <div className="grid grid-cols-2 ">
                        <div className="col-span-1 ">
                            <div className="flex flex-col gap-2 ">
                                <b>{t('asset.schema')}:</b>
                                <b>{t('asset.author')}:</b>
                                <b>{t('asset.lowest_available_price')}:</b>
                                <b>{t('asset.average_bought')}:</b>
                                <b>{t('asset.last_sold')}:</b>
                            </div>
                        </div>
                        <div className="col-span-1 ">
                            <div className="flex flex-col gap-2 ">
                                <div>{schema}</div>
                                <div>{collection.collectionName}</div>
                                <div>
                                    {template?.stats?.floorPrice ? (
                                        <span
                                            className="cursor-pointer text-primary hover:underline"
                                            onClick={() =>
                                                setSellPrice(
                                                    template?.stats?.floorPrice,
                                                )
                                            }
                                        >
                                            {formatNumber(
                                                template?.stats?.floorPrice,
                                            )}{' '}
                                            WAX{' '}
                                        </span>
                                    ) : (
                                        ''
                                    )}
                                </div>
                                <div>
                                    {template?.stats?.averageWaxPrice ? (
                                        <span
                                            className="cursor-pointer text-primary hover:underline"
                                            onClick={() =>
                                                setSellPrice(
                                                    template?.stats
                                                        ?.averageWaxPrice,
                                                )
                                            }
                                        >
                                            {Math.round(
                                                template?.stats
                                                    ?.averageWaxPrice * 100,
                                            ) / 100}{' '}
                                            WAX{' '}
                                        </span>
                                    ) : (
                                        ''
                                    )}{' '}
                                    {template?.stats?.averageUsdPrice ? (
                                        <span className="usd">
                                            ($
                                            {formatNumber(
                                                template?.stats
                                                    ?.averageUsdPrice,
                                            )}
                                            )
                                        </span>
                                    ) : (
                                        ''
                                    )}
                                </div>
                                <div>
                                    {template?.stats?.lastSold?.priceWax ? (
                                        <span
                                            className="cursor-pointer text-primary hover:underline"
                                            onClick={() =>
                                                setSellPrice(
                                                    template?.stats?.lastSold
                                                        ?.priceWax,
                                                )
                                            }
                                        >
                                            {Math.round(
                                                template?.stats?.lastSold
                                                    ?.priceWax * 100,
                                            ) / 100}{' '}
                                            WAX{' '}
                                        </span>
                                    ) : (
                                        ''
                                    )}{' '}
                                    {template?.stats?.lastSold?.priceUsd ? (
                                        <span className="usd">
                                            ($
                                            {formatNumber(
                                                template?.stats?.lastSold
                                                    ?.priceUsd,
                                            )}
                                            )
                                        </span>
                                    ) : (
                                        ''
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Card>
            </div>
            <Card className="p-4 mb-4 ">
                {symbol ? (
                    <div className="mx-auto mb-4 text-sm font-bold 2xl:text-xl">
                        {t(
                            'popups.are_you_sure_you_want_to_sell_x_assets_for_amount',
                            {
                                x: sellAmount,
                                asset: name,
                                amount: `${formatNumber(
                                    sellAmount * sellPrice,
                                )}`,
                            },
                        )}
                    </div>
                ) : (
                    <div className="mx-auto mb-4 text-sm font-bold 2xl:text-xl">
                        {sellMultiple
                            ? t(
                                  'popups.are_you_sure_you_want_to_sell_x_assets_for_amount_y_each',
                                  {
                                      x: amountToSell,
                                      asset: name,
                                      amount: `${formatWAX(
                                          amountToSell * sellPrice,
                                      )}`,
                                      y: `${formatWAX(sellPrice)}`,
                                  },
                              )
                            : t(
                                  'popups.are_you_sure_you_want_to_sell_asset_for_amount',
                                  {
                                      asset: name,
                                      amount: `${formatNumber(sellPrice)} WAX`,
                                  },
                              )}
                    </div>
                )}
            </Card>
            <div className={'relative'}>
                {error ? (
                    <ErrorMessage error={error} onClick={() => setError('')} />
                ) : (
                    ''
                )}
                {allAssets && allAssets.length > 1 && (
                    <Card className="p-4 mb-4 ">
                        <div className="grid w-full grid-cols-2 mb-2">
                            <FormControlLabel
                                fullWidth={true}
                                size="large"
                                labelPlacement="top"
                                label={`You own multiple ${name}. Sell Multiple Assets?`}
                                control={
                                    <Switch
                                        checked={sellMultiple}
                                        onChange={changeSellMultiple}
                                        disabled={allAssets.length < 2}
                                    />
                                }
                            />
                            {sellMultiple && (
                                <AmountSlider
                                    title={t('popups.amount_to_sell')}
                                    disabled={!sellMultiple}
                                    currentAmount={amountToSell}
                                    changeAmount={changeAmountToSell}
                                    increaseAmount={increaseAmountToSell}
                                    decreaseAmount={decreaseAmountToSell}
                                    max={allAssets.length}
                                />
                            )}
                        </div>
                    </Card>
                )}
                <div className="grid grid-cols-2 gap-2 mb-4">
                    <Input
                        variant="faded"
                        label={
                            symbol || amountToSell
                                ? t('asset.price_for_one')
                                : t('asset.price')
                        }
                        onChange={changePrice}
                        value={sellPrice ? sellPrice : ''}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    WAX
                                </InputAdornment>
                            ),
                        }}
                        error={!floatRegEx.test(sellAmount)}
                    />
                    {symbol ? (
                        <div className={'ml-2'}>
                            <Input
                                variant="faded"
                                type="number"
                                onWheel={(e) => e.target.blur()}
                                label={t('asset.amount')}
                                onChange={changeAmount}
                                value={sellAmount ? sellAmount : ''}
                                error={!sellAmount}
                            />
                        </div>
                    ) : (
                        ''
                    )}
                </div>

                <div
                    className={
                        'w-full xl:w-1/2 flex mt-4 xl:mt-0 gap-4 justify-center lg:justify-start'
                    }
                >
                    <MainButton
                        size="medium"
                        onClick={cancel}
                        className={
                            'w-32 max-w-32 bg-red-500 lg:w-26 lg:max-w-26 2xl:w-32 2xl:max-w-32'
                        }
                        padding={'pl-0 '}
                    >
                        {t('asset.cancel')}
                    </MainButton>
                    <MainButton
                        size="medium"
                        onClick={sellMultiple ? multiSell : sell}
                        disabled={sellMultiple && amountToSell === 0}
                        className={
                            'w-32 max-w-32 lg:w-26 lg:max-w-26 2xl:w-32 2xl:max-w-32'
                        }
                        padding={'pl-0 xl:pl-4'}
                    >
                        {t('asset.sell')}
                    </MainButton>
                </div>
            </div>
            <div className={'h-20'}>
                {collectionFee || collectionFee === 0 ? (
                    <div className="grid grid-cols-2 gap-2 pt-4 mt-4 ml-auto text-sm leading-3 md:grid-cols-4 text-start">
                        <div className="flex flex-col gap-2">
                            <div className="font-semibold ">
                                {t('popups.market_fee')}:
                            </div>
                            <div>2%</div>
                        </div>
                        <div className="flex flex-col gap-2 ">
                            <div className="font-semibold ">
                                {t('popups.defi_fee')}:
                            </div>
                            <div>1%</div>
                        </div>
                        <div className="flex flex-col gap-2 ">
                            <div className="font-semibold ">
                                {t('popups.collection_fee')}:
                            </div>
                            <div>{formatNumber(collectionFee * 100)}%</div>
                            {/*  {`${t('popups.collection_fee')}: ${
                            collectionFee * 100
                        }%`} */}
                        </div>
                        <div className="flex flex-col gap-2 ">
                            <div className="font-semibold ">
                                {t('popups.your_cut')}:
                            </div>
                            <div>
                                {cut}{' '}
                                <span className=" text-1xs xs1:text-[14px]">
                                    WAX
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <LoadingIndicator />
                )}
            </div>

            {isLoading ? (
                sellMultiple ? (
                    <PopupLoadingIndicator
                        text={t('popups.signing_multiple_transactions', {
                            x: amountToSell,
                            y: Math.ceil(amountToSell / maxActions),
                        })}
                        isLoading={isLoading && sellMultiple}
                    />
                ) : (
                    <PopupLoadingIndicator
                        text={t('popups.loading_transaction')}
                        isLoading={isLoading}
                    />
                )
            ) : (
                ''
            )}
        </Popup>
    )
}

export default SellPopup
